import { formatNumber } from '../constants.js/misc';
import { getOrderLineUnitPrice } from '../constants.js/orderProductSearch';
import { DOCUMENTS_TYPES } from '../constants.js/generals';
import { getCitiesService } from '../services/misc';

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const dash = (value) => {
    const text = String(value ?? '').trim();
    return text ? escapeHtml(text) : '—';
};

const formatOrderDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return escapeHtml(date.toLocaleString('es-CO'));
};

const lineVariantLabel = (item) => {
    const options = item?.order_item_option || [];
    const names = options
        .map((opt) => opt?.product_option_value?.name)
        .filter(Boolean);
    return names.join(', ');
};

const resolveCouponDiscount = (order, subtotal, shipping) => {
    const apiTotal = Number(
        order?.total ?? order?.order_total ?? order?.grand_total ?? order?.total_amount ?? NaN
    );
    if (!Number.isNaN(apiTotal) && apiTotal >= 0) {
        const diff = subtotal + shipping - apiTotal;
        if (diff > 0.005) return diff;
    }
    const raw =
        order?.coupon_discount_amount ??
        order?.coupon_discount ??
        order?.coupon?.discount_amount ??
        order?.coupon?.discount ??
        order?.applied_coupon?.discount_amount ??
        0;
    return Math.max(0, Number(raw) || 0);
};

const resolveShippingCost = (order, store) => {
    const fromOrder = Number(order?.shipping_cost);
    if (!Number.isNaN(fromOrder) && fromOrder > 0) return fromOrder;
    if (!store?.delivery_cost_enabled) return 0;
    const fromStore = Number(store?.delivery_cost_details);
    return Number.isNaN(fromStore) ? 0 : Math.max(0, fromStore);
};

const companyUrl = (store) => {
    if (store?.domain) return String(store.domain).replace(/^https?:\/\//, '');
    if (store?.url) return `${store.url}.ecometri.shop`;
    return '';
};

const customerDocument = (order, formInfo) => {
    const form = (formInfo || []).find((item) =>
        String(item?.title || '')
            .toLowerCase()
            .includes('identificacion')
    );
    if (form?.response) return String(form.response).replace(/[{()}"]/g, '');
    const type = DOCUMENTS_TYPES[order?.customer?.document_type]?.name || order?.customer?.document_type || '';
    const dni = order?.customer?.dni || '';
    return `${type} ${dni}`.trim();
};

let citiesCache = null;

const resolveCityLabel = async (shipping) => {
    if (!shipping) return '';
    if (shipping.city?.name) {
        return [shipping.city.name, shipping.city.state].filter(Boolean).join(', ');
    }
    const cityId = shipping.city_id;
    if (!cityId || cityId === 688) return '';
    try {
        if (!citiesCache) {
            const { data } = await getCitiesService();
            citiesCache = Array.isArray(data) ? data : [];
        }
        const city = citiesCache.find((item) => String(item.id) === String(cityId));
        if (!city) return '';
        return [city.name, city.state].filter(Boolean).join(', ');
    } catch {
        return '';
    }
};

const buildInvoiceHtml = ({ order, store, formInfo, cityLabel }) => {
    const currency = store?.currency || 'COP';
    const details = Array.isArray(order?.order_details) ? order.order_details : [];
    const lines = details.map((item) => {
        const qty = Number(item?.quantity) || 0;
        const unit = getOrderLineUnitPrice(item);
        return {
            name: item?.product?.name || 'Producto',
            sku: item?.product?.sku || item?.product_variant?.sku || '',
            variant: lineVariantLabel(item),
            qty,
            unit,
            total: unit * qty,
        };
    });
    const subtotal = lines.reduce((sum, line) => sum + line.total, 0);
    const shipping = resolveShippingCost(order, store);
    const discount = resolveCouponDiscount(order, subtotal, shipping);
    const grandTotal = Math.max(0, subtotal + shipping - discount);
    const couponCode = order?.coupon_code ?? order?.coupon?.code ?? '';
    const customer = order?.customer || {};
    const shippingInfo = order?.shipping_information || {};
    const payment = order?.order_payment;
    const storePhone =
        store?.default_user_store?.mobile_phone_number ||
        order?.user_store?.mobile_phone_number ||
        '';
    const storeLine = store?.default_user_store?.name || order?.user_store?.name || '';
    const url = companyUrl(store);
    const sequence = order?.sequence ?? '';

    const lineRows = lines
        .map(
            (line) => `
      <tr>
        <td>
          <strong>${dash(line.name)}</strong>
          ${line.variant ? `<div class="muted">${dash(line.variant)}</div>` : ''}
          ${line.sku ? `<div class="muted">SKU: ${dash(line.sku)}</div>` : ''}
        </td>
        <td class="num">${line.qty}</td>
        <td class="num">${escapeHtml(formatNumber(line.unit, currency))}</td>
        <td class="num">${escapeHtml(formatNumber(line.total, currency))}</td>
      </tr>`
        )
        .join('');

    const paymentBlock = payment
        ? `
    <section>
      <h2>Pago</h2>
      <p><span>Pasarela</span> ${dash(payment?.payment_channel?.name)}</p>
      <p><span>Estado</span> ${dash(payment?.transaction_status || payment?.transaction_status_normalized)}</p>
      <p><span>Valor pagado</span> ${escapeHtml(formatNumber(Number(payment?.amount) || 0, payment?.currency || currency))}</p>
      <p><span>Referencia</span> ${dash(payment?.platform_reference)}</p>
      <p><span>Fecha</span> ${formatOrderDate(payment?.transaction_date)}</p>
    </section>`
        : '';

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title></title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #173300; margin: 0; padding: 32px; }
    h1 { font-size: 22px; margin: 0 0 4px; }
    h2 { font-size: 13px; text-transform: uppercase; letter-spacing: .04em; margin: 24px 0 8px; color: #4a5c3a; }
    p { margin: 4px 0; font-size: 13px; }
    p span { display: inline-block; min-width: 130px; color: #6b7280; }
    .header { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #173300; padding-bottom: 16px; }
    .brand { font-size: 12px; color: #4a5c3a; }
    .meta { text-align: right; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 13px; }
    th { text-align: left; border-bottom: 1px solid #c9ced6; padding: 8px 6px; color: #4a5c3a; }
    td { border-bottom: 1px solid #eef1f4; padding: 8px 6px; vertical-align: top; }
    .num { text-align: right; white-space: nowrap; }
    .totals { width: 280px; margin-left: auto; margin-top: 12px; }
    .totals td { border: 0; padding: 4px 6px; }
    .totals .grand { font-size: 16px; font-weight: 700; border-top: 2px solid #173300; }
    .muted { color: #6b7280; font-size: 12px; font-weight: 400; }
    .note { margin-top: 32px; font-size: 11px; color: #6b7280; }
    @media print { body { padding: 16mm; } .no-print { display: none !important; } }
    @page { size: A4; margin: 0; }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <h1>${dash(store?.name)}</h1>
      <div class="brand">${url ? dash(url) : ''}</div>
      ${storeLine ? `<div class="brand">${dash(storeLine)}</div>` : ''}
      ${storePhone ? `<div class="brand">WhatsApp: +${dash(storePhone)}</div>` : ''}
    </div>
    <div class="meta">
      <p><span>Pedido</span> #${dash(sequence)}</p>
      <p><span>Fecha</span> ${formatOrderDate(order?.created_at)}</p>
    </div>
  </div>

  <div class="grid">
    <section>
      <h2>Cliente</h2>
      <p><span>Nombre</span> ${dash(`${customer.first_name || ''} ${customer.last_name || ''}`)}</p>
      <p><span>Email</span> ${dash(customer.email)}</p>
      <p><span>Teléfono</span> ${customer.phone_number ? `+${dash(customer.phone_number)}` : '—'}</p>
      <p><span>Identificación</span> ${dash(customerDocument(order, formInfo))}</p>
    </section>
    <section>
      <h2>Envío</h2>
      <p><span>Dirección</span> ${dash(shippingInfo.address)}</p>
      <p><span>Detalle</span> ${dash(shippingInfo.address_detail)}</p>
      <p><span>Ciudad</span> ${dash(cityLabel)}</p>
      <p><span>País</span> ${dash(shippingInfo.country_code)}</p>
    </section>
  </div>

  <h2>Detalle del pedido</h2>
  <table>
    <thead>
      <tr>
        <th>Producto</th>
        <th class="num">Cant.</th>
        <th class="num">Precio</th>
        <th class="num">Total</th>
      </tr>
    </thead>
    <tbody>
      ${lineRows || '<tr><td colspan="4">Sin productos</td></tr>'}
    </tbody>
  </table>
  <table class="totals">
    <tr><td>Subtotal</td><td class="num">${escapeHtml(formatNumber(subtotal, currency))}</td></tr>
    <tr><td>Envío</td><td class="num">${escapeHtml(formatNumber(shipping, currency))}</td></tr>
    ${
        discount > 0
            ? `<tr><td>Cupón${couponCode ? ` (${escapeHtml(couponCode)})` : ''}</td><td class="num">- ${escapeHtml(formatNumber(discount, currency))}</td></tr>`
            : ''
    }
    <tr class="grand"><td>Total</td><td class="num">${escapeHtml(formatNumber(grandTotal, currency))}</td></tr>
  </table>
  ${paymentBlock}
</body>
</html>`;
};

const printInvoiceHtml = (html) =>
    new Promise((resolve, reject) => {
        const iframe = document.createElement('iframe');
        iframe.setAttribute('title', 'Factura del pedido');
        iframe.style.cssText =
            'position:fixed;left:-10000px;top:0;width:210mm;min-height:297mm;border:0;background:#fff;';

        const previousTitle = document.title;
        document.title = '';

        let finished = false;
        const cleanup = () => {
            if (finished) return;
            finished = true;
            document.title = previousTitle;
            if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
            }
            resolve();
        };

        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow;
        const frameDoc = iframe.contentDocument || frameWindow?.document;
        if (!frameWindow || !frameDoc) {
            document.title = previousTitle;
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            reject(new Error('No se pudo preparar la factura'));
            return;
        }

        frameDoc.open();
        frameDoc.write(html);
        frameDoc.close();

        const printWhenReady = () => {
            frameWindow.addEventListener('afterprint', cleanup);
            frameWindow.focus();
            frameWindow.print();
            setTimeout(cleanup, 60000);
        };

        setTimeout(printWhenReady, 100);
    });

export const downloadOrderInvoice = async ({ order, store, formInfo }) => {
    if (!order) {
        throw new Error('Pedido no disponible');
    }

    const cityLabel = await resolveCityLabel(order.shipping_information);
    const html = buildInvoiceHtml({ order, store, formInfo, cityLabel });
    await printInvoiceHtml(html);
};
